<template>
  <div class="page-wrapper">
    <!-- Header Section -->
    <div class="header-section">
      <img
        src="@/assets/helen-assistant-logo.svg"
        alt="Top Logo"
        class="top-logo"
      />
      <h1 class="main-title">Admin Module</h1>
      <p class="sub-title">by Worldish</p>
    </div>

    <!-- Login Section -->
    <div class="welcome-container">
      <div class="login-box">
        <img src="@/assets/helen-logo.png" alt="Logo" class="logo" />
        <h2 class="login-title">Login</h2>

        <!-- Email Input -->
        <input
          type="email"
          v-model="email"
          placeholder="Email"
          class="input-field"
          required
        />
        <p v-if="emailError" style="color: red; font-size: 0.9rem;">
          {{ emailError }}
        </p>
        
        <!-- Password Input -->
        <input
          type="password"
          v-model="password"
          placeholder="Password"
          class="input-field"
          required
        />
        <p v-if="passwordError" style="color: red; font-size: 0.9rem;">
          {{ passwordError }}
        </p>

        <!-- HEL-2291: Google reCAPTCHA v2 (always-on on helen-security Auth) -->
        <div
          v-if="captchaProvider === 'recaptcha_v2' || captchaChallenge"
          class="captcha-slot"
        >
          <p class="captcha-label">Complete the security check</p>
          <div v-if="captchaProvider === 'recaptcha_v2'" class="recaptcha-wrap">
            <div ref="recaptchaEl" id="helen-admin-recaptcha"></div>
          </div>
          <template v-else>
            <p class="captcha-hint">{{ captchaChallenge }}</p>
            <input
              type="text"
              v-model="captchaToken"
              class="input-field"
              autocomplete="off"
              placeholder="Enter code"
            />
          </template>
        </div>

        <!-- Login Button -->
        <button class="login-btn" @click="handleLogin">Login</button>
      </div>
    </div>
  </div>
</template>

<script>
const RECAPTCHA_API_TIMEOUT_MS = 15000;

function apiUsable() {
  return !!(window.grecaptcha && typeof window.grecaptcha.render === "function");
}

/** grecaptcha appears asynchronously after script load, so poll rather than trust one event. */
function pollForApi(onReady, onTimeout) {
  const deadline = Date.now() + RECAPTCHA_API_TIMEOUT_MS;
  const timer = setInterval(() => {
    if (apiUsable()) {
      clearInterval(timer);
      onReady();
    } else if (Date.now() > deadline) {
      clearInterval(timer);
      onTimeout(new Error("recaptcha_api_timeout"));
    }
  }, 50);
}

export default {
  name: "WelcomePage",
  data() {
    return {
      email: "",
      password: "",
      emailError: "",
      passwordError: "",
      captchaChallenge: null,
      captchaToken: "",
      captchaProvider: null,
      captchaSiteKey: null,
      recaptchaWidgetId: null,
      recaptchaRenderPending: false,
      recaptchaRenderQueued: false,
    };
  },
  async mounted() {
    await this.loadCaptchaConfig();
  },
  methods: {
    async loadCaptchaConfig(attempt = 1) {
      try {
        const cfg = await this.$store.dispatch("fetchAuthCaptchaConfig");
        if (!cfg) return;
        if (
          cfg.requiredAlways &&
          cfg.captchaProvider === "recaptcha_v2" &&
          cfg.captchaSiteKey
        ) {
          this.captchaProvider = "recaptcha_v2";
          this.captchaSiteKey = cfg.captchaSiteKey;
          this.captchaChallenge = cfg.captchaChallenge || "complete_recaptcha";
          this.$nextTick(() => this.renderRecaptcha());
        }
      } catch (_) {
        // A blip here leaves the form with no captcha while auth still demands one,
        // so retry once before falling back to the post-failure challenge.
        if (attempt < 2) {
          setTimeout(() => this.loadCaptchaConfig(attempt + 1), 1000);
        }
      }
    },
    resetCaptcha() {
      const keepAlways =
        this.captchaProvider === "recaptcha_v2" && this.captchaSiteKey;
      this.captchaToken = "";
      if (!keepAlways) {
        this.captchaChallenge = null;
        this.captchaProvider = null;
        this.captchaSiteKey = null;
      }
      if (window.grecaptcha && this.recaptchaWidgetId != null) {
        try {
          window.grecaptcha.reset(this.recaptchaWidgetId);
        } catch (_) {
          /* ignore */
        }
      }
      if (!keepAlways) {
        this.recaptchaWidgetId = null;
      }
    },
    loadRecaptchaScript() {
      return new Promise((resolve, reject) => {
        if (apiUsable()) {
          resolve();
          return;
        }
        const existing = document.getElementById("helen-recaptcha-api");
        if (existing) {
          // The tag may already have fired `load`, in which case a new listener
          // never resolves. Poll for the API and time out instead of hanging.
          pollForApi(resolve, reject);
          return;
        }
        const script = document.createElement("script");
        script.id = "helen-recaptcha-api";
        script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("recaptcha_script_failed"));
        document.head.appendChild(script);
      });
    },
    async renderRecaptcha() {
      // Mount and the captcha_required path can both fire; a second concurrent
      // render wipes the container and then throws, leaving an empty box.
      if (!this.captchaSiteKey) return;
      if (this.recaptchaRenderPending) {
        // Must not drop it: the in-flight call bails out when the container is
        // not in the DOM yet, and then nothing would ever render.
        this.recaptchaRenderQueued = true;
        return;
      }
      const container = this.$refs.recaptchaEl;
      // Widget still present in the live DOM: reset it instead of rendering twice.
      if (this.recaptchaWidgetId != null && window.grecaptcha
          && container && container.childElementCount > 0) {
        try {
          window.grecaptcha.reset(this.recaptchaWidgetId);
          this.captchaToken = "";
          return;
        } catch (_) {
          /* fall through and render fresh */
        }
      }
      this.recaptchaWidgetId = null;
      this.recaptchaRenderPending = true;
      try {
        await this.loadRecaptchaScript();
        await new Promise((resolve, reject) => pollForApi(
          () => window.grecaptcha.ready(() => resolve()), reject));
        const el = this.$refs.recaptchaEl;
        if (!el) return;
        // grecaptcha refuses to render into a node it has used before, so always
        // give it a brand new child.
        el.innerHTML = "";
        const host = document.createElement("div");
        el.appendChild(host);
        this.recaptchaWidgetId = window.grecaptcha.render(host, {
          sitekey: this.captchaSiteKey,
          callback: (token) => {
            this.captchaToken = token || "";
          },
          "expired-callback": () => {
            this.captchaToken = "";
          },
        });
      } catch (e) {
        console.warn("HEL-2291 admin reCAPTCHA render failed", e);
      } finally {
        this.recaptchaRenderPending = false;
        if (this.recaptchaRenderQueued) {
          this.recaptchaRenderQueued = false;
          this.$nextTick(() => this.renderRecaptcha());
        }
      }
    },
    applyCaptchaFromError(data) {
      if (!data || typeof data !== "object") return;
      const challenge =
        data.captchaChallenge || data.challenge || data.captcha;
      if (!challenge && data.message !== "login_captcha_required") return;
      this.captchaChallenge = challenge || "complete_recaptcha";
      if (data.captchaProvider === "recaptcha_v2" && data.captchaSiteKey) {
        this.captchaProvider = "recaptcha_v2";
        this.captchaSiteKey = data.captchaSiteKey;
        this.$nextTick(() => this.renderRecaptcha());
      }
    },
    async handleLogin() {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      this.emailError = "";
      this.passwordError = "";

      if (!emailPattern.test(this.email)) {
        this.emailError = "Please enter a valid email address.";
        return;
      }

      if (!this.password || this.password.trim() === "") {
        this.passwordError = "Please enter a valid password.";
        return;
      }

      // Match caregiver: always read live widget token (callback can miss edge cases)
      if (
        this.captchaProvider === "recaptcha_v2" &&
        window.grecaptcha &&
        this.recaptchaWidgetId != null
      ) {
        try {
          this.captchaToken =
            window.grecaptcha.getResponse(this.recaptchaWidgetId) || "";
        } catch (_) {
          this.captchaToken = "";
        }
      }

      if (
        this.captchaProvider === "recaptcha_v2" &&
        !this.captchaToken
      ) {
        this.passwordError = "Please complete the captcha.";
        return;
      }

      try {
        const res = await this.$store.dispatch("login", {
          email: this.email,
          password: this.password,
          captchaToken: this.captchaToken || undefined,
        });

        if (res && res.id) {
          if (res.isSuperAdmin === true) {
            this.$router.push("/phraselibrary");
          } else {
            this.$router.push("/dashboard");
          }
        } else {
          this.passwordError =
            res?.message || "Login failed. Please check your credentials.";
          this.applyCaptchaFromError(res);
          this.resetCaptcha();
        }
      } catch (error) {
        const data = error.response?.data;
        this.applyCaptchaFromError(data);
        this.resetCaptcha();
        if (data) {
          let backendMsg =
            typeof data === "string" ? data : data.message;
          if (
            backendMsg &&
            String(backendMsg).toLowerCase().includes("bad credentials")
          ) {
            this.passwordError =
              "The password you entered is incorrect. Please try again.";
          } else if (backendMsg === "login_failed") {
            this.passwordError =
              "Login failed. Please check your email and password.";
          } else if (backendMsg === "login_captcha_required") {
            this.passwordError = "Please complete the captcha and try again.";
          } else {
            this.passwordError =
              backendMsg || "Login failed. Please try again.";
          }
        } else if (error.message) {
          this.passwordError = error.message;
        } else {
          this.passwordError = "Unexpected error occurred. Please try again.";
        }
      }
    },
  },
};
</script>

<style scoped>
:global(html, body) {
  height: 100%;
  margin: 0;
  padding: 0;
  overflow-x: hidden;
  overflow-y: auto;
  font-family: poppins, sans-serif;
}

.page-wrapper {
  min-height: 100vh;
  min-height: 100dvh;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 1rem 0 2rem;
  background-image: url("@/assets/login-bg-desktop.svg");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.header-section {
  text-align: center;
  margin-top: 0.5rem;
  flex-shrink: 0;
}

.top-logo {
  width: 100px;
  margin-bottom: 0.5rem;
}

.main-title {
  font-size: 2rem;
  font-weight: bold;
  color: #003e58;
  margin: 0;
}

.sub-title {
  font-size: 1rem;
  color: #028ac4;
  margin: 0.2rem 0 1rem 0;
}

.welcome-container {
  flex: 1 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 15px;
  padding: 0.5rem 1rem 1rem;
}

@media (max-height: 800px) {
  .welcome-container {
    align-items: flex-start;
  }

  .header-section {
    margin-top: 0;
  }

  .top-logo {
    width: 72px;
  }

  .main-title {
    font-size: 1.5rem;
  }

  .login-box {
    min-height: 0;
    padding: 1.5rem 1.5rem;
  }
}

.login-box {
  background: rgba(255, 255, 255, 0.9);
  padding: 2.5rem 2rem;
  border-radius: 12px;
  box-shadow: 0px 4px 15px rgba(0, 0, 0, 0.1);
  width: 320px;
  max-width: calc(100vw - 2rem);
  min-height: 400px;
  text-align: center;
  flex-shrink: 0;
}

.logo {
  width: 120px;
  margin-bottom: 0.5rem;
  border-radius: 50px;
}

.login-title {
  font-size: 1.5rem;
  color: #003e58;
  margin-bottom: 1.5rem;
}

.captcha-slot {
  margin-bottom: 1rem;
  text-align: left;
}

.captcha-label {
  font-size: 0.85rem;
  color: #003e58;
  margin: 0 0 0.5rem 0;
}

.recaptcha-wrap {
  display: flex;
  justify-content: center;
  margin-bottom: 0.5rem;
  overflow: visible;
  min-height: 78px; /* reCAPTCHA v2 checkbox iframe */
  /* Google renders the anchor as a fixed 304px iframe, wider than the login
     card on small phones, so scale it down to stop it overhanging. */
  transform-origin: top center;
}

@media (max-width: 359px) {
  .recaptcha-wrap {
    transform: scale(0.85);
    min-height: 0;
    height: 66px;
  }
}

@media (max-width: 300px) {
  .recaptcha-wrap {
    transform: scale(0.75);
    height: 59px;
  }
}

.captcha-hint {
  font-size: 0.8rem;
  color: #555;
  margin: 0 0 0.5rem 0;
}

.input-field,
.login-btn {
  width: 100%;
  padding: 0.7rem;
  margin-bottom: 1rem;
  font-size: 14px;
  border-radius: 6px;
  box-sizing: border-box;
}

.input-field {
  border: 1px solid #ccc;
  outline: none;
}

.input-field:focus {
  border-color: #007bff;
}

.login-btn {
  background: #007bff;
  color: white;
  border: none;
  font-size: 15px;
  cursor: pointer;
}

.login-btn:hover {
  background: #0056b3;
}
</style>
